// 02.5-calendar.md §5, paso "b" -- constante compartida entre el container (matemática de
// drag/resize) y las vistas presentacionales extraídas (`CalendarTimeView`/`TimeAreaSvgOverlay`,
// posicionamiento absoluto de eventos/bandas). Antes vivía como `const HOUR_HEIGHT = 100`
// local a `ComponentCalendar.jsx`.
export const HOUR_HEIGHT = 100;

// `features/calendar/lib/` — helpers puros compartidos por los containers del feature
// (02.5-calendar.md §5). Arranca con `buildEventPayload` (02.5-calendar.md §0.2/§6, paso "b
// parte 1" antes de la extracción presentacional): `NewEvent.jsx`'s `onSave` entrega un objeto
// plano en camelCase (`{id, title, start, end, calendarId, ...}`); el backend espera
// snake_case (`start_time`/`end_time`/`calendar_id`). `ComponentCalendar.jsx`'s propio
// `handleSave` ya arma este payload inline y correctamente — este helper es solo para los otros
// 2 mounts de `NewEvent` (`GlobalEventOverlay.jsx`/`ComponentTaskHoy.jsx`), que hasta ahora
// pasaban el objeto plano de `NewEvent` directo a `useUpdateEvent()` (que espera `{id,
// payload}`), dejando `payload` en `undefined` — un PUT sin body que el backend acepta en
// silencio sin escribir nada (`UpdateEvent.execute`'s guard de `keys.length === 0`).
export function buildEventPayload(formData) {
    return {
        title: formData.title || '(No Title)',
        description: formData.description,
        start_time: new Date(formData.start).toISOString(),
        end_time: new Date(formData.end).toISOString(),
        all_day: false,
        calendar_id: formData.calendarId || 1,
        attendees: formData.attendees || [],
        recurrence: formData.recurrence,
        actorId: formData.actorId,
    };
}

// 02.5-calendar.md §5, paso "b" -- movidos verbatim desde ComponentCalendar.jsx, sin
// dependencias de closure.
export function getViewRange(viewType, date) {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    if (viewType === 'month') {
        start.setDate(1);
        const day = start.getDay();
        start.setDate(start.getDate() - day);
        end.setMonth(end.getMonth() + 1, 0);
        const endDay = end.getDay();
        end.setDate(end.getDate() + (6 - endDay));
    } else if (viewType === 'week') {
        const day = start.getDay();
        start.setDate(start.getDate() - day);
        end.setDate(start.getDate() + 6);
    } else if (viewType === '3day') {
        end.setDate(start.getDate() + 2);
    }
    return { start, end };
}

export function decimalToTime(dec) {
    const hrs = Math.floor(dec);
    const mins = Math.round((dec - hrs) * 60);
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

export function timeToDecimal(timeStr) {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return h + (m / 60);
}

// 02.5-calendar.md §5, paso "c" -- movido desde ComponentCalendar.jsx. Ya era puro (solo lee sus
// propios parámetros, `event`/`targetUser`, ningún closure del container) -- sin cambio de firma.
export function isUserInvited(event, targetUser) {
    if (!targetUser) return false;
    const creator = event.created_by || event.createdBy;
    if (creator && String(creator) === String(targetUser.id || targetUser.dbId)) return false;

    if (event.attendees && Array.isArray(event.attendees)) {
        const tEmail = (targetUser.email || '').toLowerCase().trim();
        const tNick = (targetUser.nick || targetUser.name || '').toLowerCase().trim();

        return event.attendees.some(a => {
            const aEmail = (a.email || '').toLowerCase().trim();
            const aName = (a.name || '').toLowerCase().trim();

            // Flexible match: Exact email OR similar email OR name match if email missing
            if (aEmail && tEmail && aEmail === tEmail) return true;
            if (aName && tNick && aName === tNick) return true;

            return false;
        });
    }
    return false;
}

// 02.5-calendar.md §5, paso "c" -- movido desde ComponentCalendar.jsx, convertido a función pura
// con un segundo parámetro `ctx` que agrupa lo que antes leía por closure
// (`user`/`members`/`selectedUserIds`/`otherUsersState`/`showTasks`/`calendarColors`/
// `isCalendarVisible`/`calendarsList`/`showTaggedEvents`). El container arma `ctx` inline en
// cada render (no memoizado -- no es dependencia de ningún `useMemo`, se usa nomás para esta
// llamada) y sigue exponiendo un wrapper `(e) => isEventVisible(e, ctx)` de un solo argumento a
// `CalendarMonthView`/`eventsByDayMap`, así ningún consumidor externo cambia de contrato.
export function isEventVisible(e, ctx) {
    const { user, members, selectedUserIds, otherUsersState, showTasks, calendarColors, isCalendarVisible, calendarsList, showTaggedEvents } = ctx;

    // --- TASKS ---
    if (e.type === 'task') {
        const assigneeLower = e.task_assignee ? String(e.task_assignee).toLowerCase().trim() : '';

        // 1. Is it MY task?
        const uNickLower = (user?.nick || user?.name || '').toLowerCase().trim();
        if (uNickLower && assigneeLower.includes(uNickLower)) {
            if (showTasks) return true;
        }

        // 2. Is it a task of a SELECTED OTHER USER?
        for (const member of members) {
            if (selectedUserIds.has(String(member.id)) && String(member.id) !== String(user?.dbId)) {
                const state = otherUsersState[member.id] || { mainVisible: true, showTasks: true };
                const mNickLower = (member.name || '').toLowerCase().trim();
                if (state.mainVisible && state.showTasks && mNickLower && assigneeLower.includes(mNickLower)) {
                    return true;
                }
            }
        }
        return false;
    }

    // --- CALENDAR EVENTS ---

    // 1. Direct visibility (Owner or Shared)
    if (calendarColors[e.calendar_id] && isCalendarVisible(e.calendar_id)) return true;

    // 2. Guest visibility (If invited and Personal Calendar matches)
    // Check for logged-in user
    const myPersonalCalId = user?.calendar_personal || calendarsList.find(c => c.name === 'Personal' && String(c.user_id) === String(user?.dbId))?.id;
    if (myPersonalCalId && isCalendarVisible(myPersonalCalId) && showTaggedEvents) {
        if (isUserInvited(e, user)) return true;
    }

    // Check for SELECTED OTHER USERS
    for (const member of members) {
        if (selectedUserIds.has(String(member.id)) && String(member.id) !== String(user?.dbId)) {
            const state = otherUsersState[member.id] || { mainVisible: true, showTaggedEvents: true };
            if (state.mainVisible && state.showTaggedEvents) {
                if (isUserInvited(e, member)) return true;
            }
        }
    }

    return false;
}

// 02.5-calendar.md §5, paso "c" -- movido desde ComponentCalendar.jsx (algoritmo de
// clustering/overlap por día). Convertido a función pura: recibe `isEventVisible` ya resuelto
// (el container arma su propio wrapper de un argumento sobre `isEventVisible(e, ctx)`) en vez de
// reconstruir el `ctx` acá -- así este helper no necesita conocer la forma de `visibilityCtx`.
// Antes recalculado (y mutando los objetos de `visibleEvents` con `ev._col`/`_widthPct`/`_leftPct`
// directo, un antipatrón de React) en cada render dentro del JSX de `renderTimeView`. El container
// sigue siendo quien memoiza el resultado (`useMemo`) y decide las deps reales.
export function buildEventsByDayMap(timeViewCols, visibleEvents, tempEvent, isEventVisible) {
    const map = new Map();
    timeViewCols.forEach(date => {
        let dayEvents = visibleEvents.filter(e =>
            isEventVisible(e) &&
            e.start.getDate() === date.getDate() &&
            e.start.getMonth() === date.getMonth() &&
            e.start.getFullYear() === date.getFullYear() &&
            // Si `tempEvent` es la vista previa en vivo de un evento EXISTENTE (edición o
            // drag, no creación), excluir la entrada real de SU día -- sin importar si ese
            // día es el que se está armando acá ahora mismo. Si el filtro de exclusión
            // viviera solo dentro del `if` de abajo (día destino), un drag/edición que
            // cambia de día dejaba un duplicado fantasma en el día de origen.
            (!tempEvent || e.id !== tempEvent.id)
        );

        if (tempEvent &&
            tempEvent.start.getDate() === date.getDate() &&
            tempEvent.start.getMonth() === date.getMonth()) {
            dayEvents = [...dayEvents, tempEvent];
        }

        dayEvents = [...dayEvents].sort((a, b) => a.start - b.start);

        const clusters = [];
        let currentCluster = [];
        let clusterEnd = null;
        dayEvents.forEach(ev => {
            if (clusterEnd === null || ev.start < clusterEnd) {
                currentCluster.push(ev);
                if (clusterEnd === null || ev.end > clusterEnd) clusterEnd = ev.end;
            } else {
                clusters.push(currentCluster);
                currentCluster = [ev];
                clusterEnd = ev.end;
            }
        });
        if (currentCluster.length > 0) clusters.push(currentCluster);

        const positioned = [];
        clusters.forEach(cluster => {
            const columns = [];
            const colByEvent = new Map();
            cluster.forEach(ev => {
                let placed = false;
                for (let i = 0; i < columns.length; i++) {
                    if (columns[i][columns[i].length - 1].end <= ev.start) {
                        columns[i].push(ev);
                        colByEvent.set(ev, i);
                        placed = true;
                        break;
                    }
                }
                if (!placed) {
                    colByEvent.set(ev, columns.length);
                    columns.push([ev]);
                }
            });
            const maxCols = columns.length;
            cluster.forEach(ev => {
                const col = colByEvent.get(ev);
                positioned.push({ ...ev, _col: col, _widthPct: 97 / maxCols, _leftPct: (col * (97 / maxCols)) + 1.5 });
            });
        });

        map.set(date.toDateString(), positioned);
    });
    return map;
}
